import { Injectable } from '@angular/core';
import { format } from 'date-fns';
import { BackendPrice, CartRow, CartRowPrice, CustomerProductPrice, GungStringConverterService, ProductPriceContext } from 'gung-standard';
import { JeevesPriceService } from 'gung-standard-jeeves';
import { filter, first, forkJoin, map, mergeMap, Observable, of, switchMap } from 'rxjs';
import { environment } from 'projects/tylo/src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TyloPriceService extends JeevesPriceService {

  protected buildProductIdDeliveryDateMap(cartRows: CartRow[]): { productId: string, deliveryDate: string }[] {
    const res: { productId: string, deliveryDate: string }[] = [];
    for (const cartRow of cartRows) {
      let deliveryDate: string = cartRow.extra?.orp?.ordberlevdat || '';

      if (!deliveryDate && !!cartRow.extra?._deliveryPlan) {
        const dates: Date[] = Object.keys(cartRow.extra?._deliveryPlan).map(d => new Date(d)).sort((a,b)=>new Date(b).getTime() - new Date(a).getTime());
        deliveryDate = dates.length > 0 ? format(dates[0], 'yyyy-MM-dd') : '';
      }

      if (!!deliveryDate) {
        res.push({ productId: cartRow.productId, deliveryDate: deliveryDate });
      } else {
        res.push({ productId: cartRow.productId, deliveryDate: '2099-12-31' });
      }
    }

    return res;
  }

  public getCartRowPricesObservable(cartRows: CartRow[]): Observable<CartRowPrice[]> {
    const productIdQuantityMap: { [productId: string]: number } = {};
    const productIdPromises: Observable<string>[] = [];
    cartRows.forEach(cartRow => {
      switch (this.qtyGroupingLevel) {
        case 'MODEL': {
          productIdPromises.push(this.productService.getModelId(cartRow.productId));
          break;
        }
        case 'VARIANT': {
          productIdPromises.push(this.productService.getVariantProductId(cartRow.productId));
          break;
        }
        default: {
          productIdQuantityMap[cartRow.productId] = cartRow.qty;
          break;
        }
      }
    });

    if (productIdPromises.length) {
      return forkJoin(productIdPromises).pipe(
        switchMap(productIds => {
          this.fillGroupedProductIdQuantityMap(
            cartRows,
            productIdQuantityMap,
            Object.assign({}, ...cartRows.map((cartRow, index) => ({ [cartRow.productId]: productIds[index] })))
          );
          return this.getCartRowsPricesFromProductIdQuantityMap(productIdQuantityMap, cartRows);
        })
      );
    } else {
      return this.getCartRowsPricesFromProductIdQuantityMap(productIdQuantityMap, cartRows);
    }
  }

  protected getCartRowsPricesFromProductIdQuantityMap(
    productIdQuantityMap: { [productId: string]: number },
    cartRows: CartRow[]
  ): Observable<CartRowPrice[]> {
    return this.getCurrentCustomerPriceWithQty(productIdQuantityMap, cartRows).pipe(
      first(),
      map(prices => {
        return this.getCartRowPrices(prices, cartRows);
      })
    );
  }

  getCurrentCustomerPriceWithQty(input: { [productId: string]: number }, cartRows?: CartRow[]): Observable<CustomerProductPrice[]> {
    return this.customerService
      .getSelectedCustomer()
      .pipe(filter(cust => !!cust), switchMap(cust => this.getCustomerPricesWithQtys(input, cust.id, cartRows)));
  }

  getCustomerPricesWithQtys(
    input: { [productId: string]: number },
    customerId: string,
    cartRows?: CartRow[]
  ): Observable<CustomerProductPrice[]> {
    return this.internalHttpGetCustomerPrices(Object.keys(input), customerId, input, this.buildProductIdDeliveryDateMap(cartRows)).pipe(
      switchMap(resp =>
        forkJoin({
          prices: of(resp),
          customer: this.customerService.getSelectedCustomer().pipe(first())
        })
      ),
      map(({ prices, customer }) => {
        return Object.values(prices).map(price =>
          this.mapCustomerProductPrice(price, customer, input[price.productId])
        );
      })
    );
  }

  protected internalHttpGetCustomerPrices(
    productIds: string[],
    customerId: string,
    productQty?: { [productId: string]: number },
    productIdDeliveryDateMap?: { productId: string, deliveryDate: string }[]
  ): Observable<{ [productId: string]: BackendPrice }> {
    if (!!productIdDeliveryDateMap && productIdDeliveryDateMap.length > 0 && environment.projectName === 'tylo') {
      return forkJoin(
        productIdDeliveryDateMap.map(pidd => this.http.get<BackendPrice>(`json/tylo/product-price/${customerId}/${pidd.productId}?date=${pidd.deliveryDate}`, { headers: { maxAge: '86400' } }).pipe(first()))
      ).pipe(
        switchMap(prices => {
          const result: { [productId: string]: BackendPrice } = {};
          for (const price of prices) {
            result[price.productId] = price;
          }
          return of(result);
        })
      );
    }

    return super.internalHttpGetCustomerPrices(productIds, customerId);
  }
}
